import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useDocsSidebar } from '../context/DocsSidebarContext';

interface NavItem {
  titleKey: string;
  slug: string;
}

interface NavSection {
  titleKey: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    titleKey: 'sections.getting_started',
    items: [
      { titleKey: 'pages.what_is.title', slug: 'o-que-e' },
      { titleKey: 'pages.fundamental_concepts.title', slug: 'conceitos-fundamentais' },
      { titleKey: 'pages.overview.title', slug: 'visao-geral' },
    ],
  },
  {
    titleKey: 'sections.using_system',
    items: [
      { titleKey: 'pages.projects.title', slug: 'projetos' },
      { titleKey: 'pages.meetings.title', slug: 'atas-de-reuniao' },
      { titleKey: 'pages.topics_items.title', slug: 'topicos-e-itens' },
      { titleKey: 'pages.developments.title', slug: 'desenvolvimentos' },
      { titleKey: 'pages.participants.title', slug: 'participantes' },
    ],
  },
  {
    titleKey: 'sections.tracking',
    items: [
      { titleKey: 'pages.minutes_map.title', slug: 'mapa-de-atas' },
      { titleKey: 'pages.preview_export.title', slug: 'preview-e-exportacao' },
    ],
  },
  {
    titleKey: 'sections.collaboration',
    items: [
      { titleKey: 'pages.invite_team.title', slug: 'convidar-equipe' },
      { titleKey: 'pages.organization_collaboration.title', slug: 'organizacao-e-colaboracao' },
    ],
  },
  {
    titleKey: 'sections.reference',
    items: [
      { titleKey: 'pages.customization.title', slug: 'personalizacao' },
      { titleKey: 'pages.best_practices.title', slug: 'boas-praticas-atas' },
      { titleKey: 'pages.news.title', slug: 'novidades' },
    ],
  },
];

interface DocsSidebarProps {
  onNavigate?: () => void;
}

export function DocsSidebar({ onNavigate }: DocsSidebarProps) {
  const location = useLocation();
  const { t } = useTranslation('docs');
  const { t: tCommon } = useTranslation('common');
  const appName = tCommon('app_name');
  const { collapsed, toggleCollapse } = useDocsSidebar();

  const isActive = (slug: string) => {
    return location.pathname === `/docs/${slug}`;
  };

  // Helper to translate with appName interpolation
  const translateWithApp = (key: string) => {
    return t(key, { appName });
  };

  // Estado colapsado
  if (collapsed) {
    return (
      <aside className="hidden border-r border-border bg-background lg:flex flex-col flex-shrink-0 w-12 sticky top-0 h-[calc(100vh-57px)] overflow-hidden">
        <div className="p-2 border-b border-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCollapse}
            aria-label={t('sidebar.expand')}
            className="w-full h-8"
          >
            <ChevronRight size={18} />
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center pt-3 gap-2">
          <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center" title={t('sidebar.documentation')}>
            <BookOpen size={16} className="text-muted-foreground" />
          </div>
          <span className="text-xs text-muted-foreground">{navigationSections.reduce((acc, s) => acc + s.items.length, 0)}</span>
        </div>
      </aside>
    );
  }

  return (
    <aside className="hidden border-r border-border bg-background lg:flex flex-col flex-shrink-0 w-64 sticky top-0 h-[calc(100vh-57px)] overflow-hidden">
      <ScrollArea className="h-full flex-1">
        <div className="flex flex-col gap-1 p-4">
          {/* Header com Logo e botão colapsar */}
          <div className="mb-4 flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 px-2"
            >
              <img
                src="/logo-stant.svg"
                alt="Stant"
                className="h-7 w-auto dark:brightness-0 dark:invert"
              />
            </Link>
            <button
              onClick={toggleCollapse}
              className="p-1.5 hover:bg-muted rounded-md"
              aria-label={t('sidebar.collapse')}
            >
              <ChevronLeft size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex flex-col gap-6">
            {navigationSections.map((section) => (
              <div key={section.titleKey} className="flex flex-col gap-1">
                {/* Section Title */}
                <h4 className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t(section.titleKey)}
                </h4>

                {/* Section Items */}
                <div className="flex flex-col">
                  {section.items.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/docs/${item.slug}`}
                      onClick={onNavigate}
                      className={cn(
                        'relative rounded-md px-2 py-1.5 text-sm transition-colors',
                        isActive(item.slug)
                          ? 'bg-primary/10 font-medium text-primary'
                          : 'text-foreground hover:bg-muted'
                      )}
                    >
                      {translateWithApp(item.titleKey)}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </ScrollArea>
    </aside>
  );
}
